import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth.store";

/**
 * Vide le cache de requêtes dès que l'identité authentifiée change.
 *
 * Le cache de TanStack Query est indexé par clé de requête, jamais par utilisateur : rien n'y
 * distingue « les classes de l'enseignante » des « classes de l'élève ». Tant qu'un seul compte
 * vit dans l'onglet, c'est sans conséquence. Ça cesse de l'être au changement d'identité — reprise
 * d'un compte après déconnexion, et surtout **usurpation d'identité par un administrateur**, où
 * l'ancienne et la nouvelle identité se succèdent sans rechargement de page.
 *
 * Deux effets recherchés, dans cet ordre d'importance :
 *
 *  1. ne jamais rendre à une identité les données mises en cache par une autre ;
 *  2. annuler les requêtes encore en vol, qui repartiraient sinon avec le jeton de la nouvelle
 *     identité alors qu'elles ont été décidées pour l'ancienne — mécanisme confirmé par le backend
 *     le 17/07 : quatre appels à des routes enseignantes émis avec le jeton d'un compte élève,
 *     `impersonated_by` renseigné.
 *
 * Ce que cette purge ne fait **pas** : elle n'empêche pas un écran resté monté de relancer sa
 * lecture juste après. Vider le cache provoque même ce rafraîchissement pour tout observateur
 * actif. C'est assumé, parce que la protection contre l'appel lui-même n'a pas sa place ici mais
 * dans les gardes de rôle des hooks concernés (cf. `canReadClassroomResources`) : les deux
 * changements sont complémentaires, aucun ne suffit seul.
 *
 * L'identité est dérivée du rôle et de l'identifiant utilisateur, pas du jeton : un simple
 * renouvellement de jeton pour le même compte ne doit pas coûter un vidage complet.
 */
export function QueryIdentityGuard() {
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const previousIdentityRef = useRef<string | null>(null);

  useEffect(() => {
    const identity = isAuthenticated ? `${role}:${userId ?? "unknown"}` : null;
    const previous = previousIdentityRef.current;
    previousIdentityRef.current = identity;

    // Premier rendu : rien ne précède, donc rien à purger — l'hydratation du store ne doit pas
    // jeter un cache légitimement rempli par le chargement en cours.
    if (previous === null || previous === identity) return;

    void queryClient.cancelQueries();
    queryClient.clear();
  }, [queryClient, role, userId, isAuthenticated]);

  return null;
}
